<template>
  <div class="widget__content-center widget__content-center--flex">
    <div class="unique">
      <div class="unique-left">
        <div class="widget__header-text">
          Total OTS visitors split by Engaged and<br />Passerby across the entire event.
        </div>

        <div class="widget__label">Total<br />Opportunity to See (OTS)</div>

        <div class="unique-total">
          {{ formatNumber(totalVisitors, 0) }}
        </div>

        <div v-if="chartSeries.length" class="chart-wrapper">
          <apexchart
            height="100%"
            :options="chartOptions"
            :series="chartSeries"
            type="bar"
            width="400px"
          />
        </div>
      </div>

      <div class="unique-center"></div>

      <div class="unique-right">
        <div class="conversion-rate">{{ formatNumber(usableData['Conversion Rate'], 2) }}%</div>

        <div class="widget__label">
          <IconConversionRate />
          Conversion Rate
        </div>

        <p>The percentage of OTS visitors who Engaged at your event</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { barColors } from '@/utils/chartColors';
import type { DataUniqueVisitors } from '@/types/Widget';

import type { BaseChartOptions } from '@/utils/chartSettings';
import { getDefaultChartOptions } from '@/utils/chartSettings';
import IconConversionRate from '@/icons/widgets/IconConversionRate.svg?component';
import { formatNumber } from '@/utils/formatNumbers';
const props = defineProps<{
  widget: DataUniqueVisitors;
}>();

const usableData = computed(() => props.widget.data['Site']['Total']);

const engagedVisitors = computed(() => usableData.value['Engaged Visitors']);
const passerbyVisitors = computed(() => usableData.value['Passerby Visitors']);

const totalVisitors = computed(() => engagedVisitors.value + passerbyVisitors.value);

const defaultChartOptions = getDefaultChartOptions(props.widget.code_name);

const chartOptions = computed<BaseChartOptions>(() => ({
  ...defaultChartOptions.value,
  chart: {
    ...defaultChartOptions.value.chart,
    type: 'bar',
    id: 'meshh-chart-site-total',
    toolbar: {
      show: false,
    },
    stacked: true,
  },
  colors: barColors,
  xaxis: {
    categories: ['Total'],
    show: false,
    axisTicks: { show: false },
  },
  yaxis: {
    show: false,
  },
  grid: {
    show: false,
  },
  legend: {
    ...defaultChartOptions.value.legend,
    floating: true,
  },
  dataLabels: {
    enabled: true,
    textAnchor: 'start',
    style: {
      colors: ['#667085'],
    },
    formatter: (val) => {
      return formatNumber(val, 0);
    },
    offsetX: 60,
    dropShadow: {
      enabled: false,
    },
  },
  tooltip: {
    y: {
      formatter: (val: number) => {
        return formatNumber(val, 0);
      },
    },
  },
}));

const chartSeries = computed(() => [
  {
    name: 'Engaged',
    data: [0, engagedVisitors.value, 0],
  },
  {
    name: 'Passerby',
    data: [0, passerbyVisitors.value, 0],
  },
]);
</script>

<style lang="scss">
@use 'sass:color';

.unique {
  @include mixins.flex-center($horiz: flex-start, $gap: variables.$sizing-lg);
  width: 100%;

  .unique-left,
  .unique-right {
    flex-basis: calc(50% - 26px);
  }

  .unique-total {
    line-height: variables.$sizing-sm;
  }

  .chart-wrapper {
    height: 195px;
  }
}

.unique-center {
  flex-basis: variables.$sizing-xxxs;
  flex-grow: 0;
  flex-shrink: 0;
  position: relative;

  &::before {
    background: variables.$primitives-color-border-200;
    bottom: variables.$sizing-xxxl;
    content: '';
    left: 1.5px;
    position: absolute;
    top: variables.$sizing-xxxl;
    right: 1.5px;
    width: 1px;
  }
}

.unique-total {
  @include typography.font-display($size: lg, $weight: bold);
  line-height: variables.$sizing-sm;
  text-align: center;
}

.unique {
  .apexcharts-xaxis {
    display: none;
  }

  .apexcharts-canvas {
    margin: 0 auto;
  }

  .widget__label {
    margin-bottom: variables.$sizing-xs;

    svg {
      margin-right: variables.$sizing-xxs;
    }
  }
}

.unique-right {
  @include mixins.flex-center($horiz: center);
  flex-direction: column;
  text-align: center;

  // TODO: Change colors based on percentage
  .conversion-rate {
    @include mixins.rounded-border($color: variables.$primitives-color-primary-500);
    @include sizing.margin('lg', 'auto');
    @include sizing.padding();
    @include typography.font-display($size: md, $weight: bold);
    background: color.adjust(variables.$primitives-color-primary-100, $lightness: 20%);
    color: variables.$primitives-color-primary-500;
    display: inline-block;
  }

  p {
    @include sizing.margin('xxl', 0);
    color: variables.$primitives-color-tertiary-400;
  }
}
</style>
